/**
 * Centralna konfiguracja kafelków i kategorii panelu administracyjnego.
 *
 * Każdy kafelek definiuje: slug (URL), tytuł, opis, ikonę, uprawnienie,
 * kategorię, kolor akcentu, tag, oraz typ nawigacji (inline component vs route).
 *
 * Kategorie definiują grupy modułów wyświetlane na stronie głównej admina.
 */
import React from "react";
import {
    FiEye,
    FiUsers,
    FiHardDrive,
    FiGlobe,
    FiBook,
    FiLayers,
    FiTag,
    FiDatabase,
    FiEdit3,
    FiImage,
    FiBell,
    FiMessageSquare,
    FiBriefcase,
    FiFileText,
    FiAward,
    FiSettings,
    FiMonitor,
    FiClipboard,
    FiPackage,
    FiBarChart2,
    FiKey,
    FiCast,
    FiHelpCircle,
    FiBookOpen,
    FiCalendar,
    FiShield,
    FiTrello,
    FiCloud,
    FiUserPlus,
    FiActivity,
    FiTrendingUp,
    FiWifi,
    FiGitMerge,
    FiRefreshCw,
    // [PAKIET 2026-09-02] Rejestr wydań (K/I-a), offboarding (I-c), oferty pracy (J).
    FiArchive,
    FiUserMinus,
    FiHash,
    // [F-8 2026-09-11] Menedżer haseł — kafelek wglądu centralnego.
    FiLock,
    FiSmartphone,
    // [P1 2026-10-01] „Wykaz KSC” — zegar (obowiązek 14 dni), inna ikona niż segregator procedur.
    FiClock,
} from "react-icons/fi";

// ── KATEGORIE PANELU ADMINISTRACYJNEGO ──────────────────────────
//
// [zgłoszenie właściciela 2026-09-05 noc] „Uporządkuj mi /admin — jest tutaj za duży chaos, pozycje są błędnie
// przypisane, trzeba zmienić organizację w kategoriach, które powinny być zaktualizowane."
//
// BYŁO ŹLE: kategoria „Konfiguracja Systemu i Integracje" urosła do 13 kafelków i stała się workiem na
// wszystko, czego nie było gdzie dopisać — obok integracji Microsoft 365 i migracji bazy leżały tam builder
// stopek e-mail, formularze kontaktowe, regulamin platformy, klauzula RODO pierwszego logowania i KREATOR
// KAFELKÓW ZGŁOSZEŃ. Administrator szukający konfiguracji helpdesku trafiał do „systemu", a szukający treści
// prawnych — też do „systemu".
//
// TERAZ: kategoria = OBSZAR, którym ktoś realnie zarządza, i pokrywa się z modułami obsługi
// (IT · Kadry · Compliance · Treści · L&D · Narzędzia · Dostęp · System). Dzięki temu ta sama nazwa działa
// w trzech miejscach naraz: sekcja na /admin, sekcja w tablicy uprawnień i grupa kafelków modułu.
// W „Systemie i integracjach" zostaje WYŁĄCZNIE to, co dotyczy całej instalacji i nie ma właściciela
// biznesowego — reszta poszła do swojego obszaru.
//
// Kolejność kluczy = kolejność sekcji na /admin.
export const ADMIN_CATEGORIES = {
    itam: {
        label: "IT — Helpdesk i majątek (ITAM)",
        icon: <FiMonitor />,
        color: "#F59E0B",
    },
    org: {
        label: "Kadry i organizacja",
        icon: <FiUsers />,
        color: "#3B82F6",
    },
    // [2026-09-05] Sygnalista i polityki NIE są kadrami ani konfiguracją techniczną. Compliance ma własnego
    // adresata (osoby wyznaczone imiennie, need-to-know) i własne obowiązki prawne — dlatego własna sekcja.
    // [P1 2026-10-01, „Cykl życia na pasku”] Półka obowiązków prawnych firmy: zgłoszenia sygnalistów, dokumenty bezpieczeństwa
    // wymagane przez NIS2 (Procedury i bezpieczeństwo) i zgłoszenia do wykazu KSC — dwa ostatnie kafelki przeszły tu z „Narzędzi”.
    // [integracja 2026-10-01] Opis sekcji (`opis`) rysuje teraz siatka /admin pod nagłówkiem kategorii.
    compliance: {
        label: "Compliance i cyberbezpieczeństwo",
        // [integracja 2026-10-01] Siatka /admin rysuje teraz zdanie pod nagłówkiem kategorii (AdminPanelPage: `catInfo.opis`) —
        // odpowiedź na pytanie właściciela, dlaczego Wykaz KSC stoi obok procedur.
        opis: "Obowiązki prawne firmy: zgłoszenia sygnalistów, dokumenty bezpieczeństwa wymagane przez NIS2 i zgłoszenia do wykazu KSC.",
        icon: <FiShield />,
        color: "#DC2626",
    },
    content: {
        label: "Komunikacja, Treści i Wiedza",
        icon: <FiEdit3 />,
        color: "#8B5CF6",
    },
    training: {
        label: "Szkolenia i Rozwój (L&D)",
        icon: <FiBookOpen />,
        color: "#059669",
    },
    tools: {
        label: "Narzędzia i Projekty",
        icon: <FiTrello />,
        color: "#14B8A6",
    },
    access: {
        label: "Uprawnienia i Dostęp",
        icon: <FiKey />,
        color: "#EF4444",
    },
    config: {
        label: "System i integracje",
        icon: <FiSettings />,
        color: "#10B981",
    },
};

// ── Mapa uprawnień per kategoria ───────────────────────
export const CATEGORY_PERMISSIONS = {
    itam:       "AdminITAM",
    org:        "AdminOrgHR",
    compliance: "AdminOrgHR",
    content:    "AdminContent",
    training:   "AdminTraining",
    tools:      "AdminContent",
    access:     "AdminAccessManage",
    config:     "AdminConfig",
};

// ── Kafelki panelu administracyjnego ────────────────────────────
// type: "inline" → komponent renderowany w AdminModuleRouter
// type: "route"  → nawigacja do osobnej strony (routeKey w routes.js)
export const ADMIN_TILES = [
    // ═══════════════════════════════════════════════════════════
    // UPRAWNIENIA I DOSTĘP
    // ═══════════════════════════════════════════════════════════
    {
        id: "users-module",
        slug: "users",
        title: "Użytkownicy",
        description: "Konta, role i uprawnienia systemowe.",
        icon: <FiUsers />,
        permission: "AdminUsersView",
        category: "access",
        accent: "#3B82F6",
        tag: "Administracja",
        type: "inline",
        componentPath: "./admin/UsersPage",
    },
    /* [zgłoszenie właściciela 2026-09-16] „w zakładki admin — Uprawnienia i Dostęp przenieś mi to do sekcji
       System i integracje."

       Ekran, na którym NADAJE SIĘ uprawnienia, przechodzi do kategorii `config`, a ta od tej samej zmiany
       znaczy „tylko ranga administratora, i tylko tu pytamy o PIN". Trzy rzeczy zaczynają się zgadzać:
        • kto rozdaje dostępy, sam musi być administratorem — dotąd wystarczał klucz `AdminAccessManage`,
          więc delegat mógł nadawać (choć na kluczach need-to-know i tak dostawał 403);
        • ten jeden ekran zostaje za PIN-em, gdy reszta panelu PIN-u już nie wymaga;
        • „Moduły obsługi" przestają go wypisywać — i nie trzeba na to osobnego wyjątku, bo reguła jest
          jedna: kategoria `config` nie jest rzeczą przypisywaną działowi.

       W kategorii „Uprawnienia i Dostęp" zostają: Użytkownicy, Prośby o konta, Menedżer haseł — narzędzia
       pracy, które właściciel kazał zostawić przypisywalne (polecenia z 11.09 i 15.09). */
    {
        id: "access-control",
        slug: "access-control",
        title: "Uprawnienia i dostęp",
        description: "Uprawnienia per składnik nakładane na usera (zakładki admina + moduły), rangi, PIN, dostęp do domen oraz globalna widoczność menu — w jednym kombajnie.",
        icon: <FiShield />,
        permission: "AdminAccessManage",
        category: "config",
        accent: "#EF4444",
        tag: "Bezpieczeństwo",
        type: "inline",
        componentPath: "./admin/AccessControlPage",
    },
    /* [F-8, polecenie właściciela 2026-09-11] „centralny kafelek admin/password-manager
       w Uprawnieniach i dostępie".

       Klucz `SejfCentralnyWglad` jest need-to-know: ranga administratora NIE otwiera firmowych
       banków haseł. Kafelek pokazuje banki DZIAŁOWE i MODUŁOWE — sejfy osobiste pracowników są
       poza zasięgiem tego ekranu i nie otwiera ich żaden klucz. Każde ujawnienie hasła wymaga
       jednorazowego kodu SMS i zostawia ślad widoczny dla właściciela wpisu. */
    {
        id: "password-manager",
        slug: "password-manager",
        title: "Menedżer haseł",
        // [domknięcie po przeglądzie 2026-10-04] Opis mówi o imporcie z Excela — z listy kafelków ma być widać, gdzie go szukać.
        description: "Banki haseł działów i modułów: podgląd wpisów, dziennik dostępu, raport wglądów w cudze hasła oraz import haseł z Excela do banków działów (z plikiem wzorcowym). Sejfy osobiste pracowników pozostają prywatne; ujawnienie hasła wymaga kodu SMS.",
        icon: <FiLock />,
        permission: "SejfCentralnyWglad",
        category: "access",
        accent: "#0EA5E9",
        tag: "Bezpieczeństwo",
        type: "inline",
        componentPath: "./admin/PasswordManagerPage",
    },
    /* [O365-F 2026-10-04, decyzja właściciela 3.10] „Zarządzanie Office 365” — bezpieczeństwo chmury Microsoft 365 każdej spółki: konta tenantów,
       MFA, role, licencje, reguły incydentów, alerty, wyjątki na osobach, polityki tenanta. Klucze need-to-know (Office365View / Office365Manage):
       ranga administratora ich NIE daje. Wejście = KAFELEK MODUŁOWY — włączony w Uprawnienia i dostęp → Moduły obsługi daje oba klucze
       każdemu członkowi modułu (KafelkiModulow.acc_office365). Administrator bez klucza widzi kafelek ZABLOKOWANY z podpowiedzią.
       Slug `microsoft365` jest zajęty przekierowaniem — nie używać. Strona: pages/admin/office365/ZarzadzanieOffice365Page (etap 2: O365-Z). */
    {
        id: "office365-module",
        slug: "office365",
        title: "Zarządzanie Office 365",
        description: "Bezpieczeństwo chmury Microsoft 365 każdej spółki: konta, MFA, role, licencje, reguły incydentów, alerty, wyjątki na osobach i polityki tenanta.",
        icon: <FiCloud />,
        permission: "Office365View",
        category: "access",
        accent: "#0078D4",
        tag: "Microsoft 365",
        type: "inline",
        componentPath: "./admin/office365/ZarzadzanieOffice365Page",
        pokazRandzeBezKlucza: true,
        podpowiedzBezKlucza: "ranga administratora celowo go nie daje. Włącz kafelek „Zarządzanie Office 365” w Uprawnienia i dostęp → Moduły obsługi → Zgłoszenia IT → Kafelki i uprawnienia — każdy członek modułu dostanie podgląd i zarządzanie (pojedynczej osobie możesz je jawnie odebrać). Sam podgląd, bez zarządzania, daje kafelek „Office 365 — tylko podgląd”.",
    },

    // ═══════════════════════════════════════════════════════════
    // ZARZĄDZANIE ORGANIZACJĄ I HR
    // ═══════════════════════════════════════════════════════════
    {
        id: "departments-module",
        slug: "departments",
        title: "Działy",
        description: "Struktura organizacyjna i przypisania.",
        icon: <FiDatabase />,
        permission: "AdminDepartmentsManage",
        category: "org",
        accent: "#6366F1",
        tag: "Struktura",
        type: "inline",
        componentPath: "./admin/DepartmentsPage",
    },
    {
        id: "employees-module",
        slug: "employees",
        title: "Kartoteka pracowników",
        description: "Stanowiska, rangi i dane kadrowe.",
        icon: <FiBriefcase />,
        permission: "AdminUsersView",
        category: "org",
        accent: "#0EA5E9",
        tag: "Kadry",
        type: "route",
        routeKey: "adminEmployees",
    },
    {
        // [BE-6b, polecenie wlasciciela 2026-09-19] „HR ma to wiedziec i ma miec status z tego" — kafelek jest
        // jedynym miejscem, z ktorego kadry trafia do kolejki bez znajomosci adresu. Klucz TEN SAM co trasa
        // (HrAccess) — kafelek za innym kluczem niz trasa konczy sie odmowa po kliknieciu (lekcja z e-teczki).
        id: "zmiany-kadrowe-module",
        slug: "zmiany-kadrowe",
        title: "Zmiany danych kadrowych",
        description: "Kto zmienil dana kadrowa, z czego na co i skad — z oznaczeniem przejrzane/do wyjasnienia.",
        icon: <FiBriefcase />,
        permission: "HrAccess",
        category: "org",
        accent: "#0EA5E9",
        tag: "Kadry",
        type: "route",
        routeKey: "adminZmianyKadrowe",
    },
    {
        id: "e-teczka-module",
        slug: "e-teczka",
        title: "E-Teczka",
        description: "Elektroniczna teczka pracownicza — kartoteka, dokumenty A–E, ECP.",
        icon: <FiBriefcase />,
        // [UPRAWNIENIA 2026-07-27] Kafelek musi wymagać TEGO SAMEGO klucza co trasa docelowa —
        // wcześniej gatowany HrAccess, a trasa /e-teczka wymaga ModuleETeczka, więc kadrowiec
        // widział kafelek i po kliknięciu dostawał odmowę.
        permission: "ModuleETeczka",
        category: "org",
        accent: "#4F46E5",
        tag: "E-Teczka",
        type: "route",
        routeKey: "eTeczka",
    },
    {
        id: "hr-pdf-templates",
        slug: "hr-config",
        title: "Konfiguracja HR",
        description: "Kategorie, typy wniosków, szablony pism, pule saldowe, dokumenty firmowe.",
        icon: <FiFileText />,
        // [P-3] Którykolwiek: kadry (całość) albo administrator treści (tylko zakładka „Dokumenty firmowe" —
        // resztę zakładek strona chowa sama, `zakladkiDlaUprawnien`).
        permission: ["HrAccess", "AdminDocuments"],
        category: "org",
        accent: "#8B5CF6",
        tag: "Konfiguracja",
        type: "inline",
        componentPath: "./admin/HrPdfTemplatesPage",
    },
    {
        /* [N-10, polecenie właściciela 2026-09-08] „Lista MPK" — słownik kodów MPK działów i pracowników
           z WŁASNYM kluczem (access-control → Kadry i organizacja). Lista składa się sama z kodów wpisanych
           w działach i kartotekach; stąd dodaje się nowe i przypisuje z listy do działu / pracownika. */
        id: "mpk-module",
        slug: "mpk",
        title: "Lista MPK",
        description: "Kody MPK działów i pracowników w jednym słowniku: dodawanie, porządkowanie, przypisywanie z listy.",
        icon: <FiHash />,
        permission: "AdminMpkManage",
        category: "org",
        accent: "#0D9488",
        tag: "MPK",
        type: "inline",
        componentPath: "./admin/MpkPage",
    },
    {
        /* [RAPORT ZDALNY 2026-07-31] Przeniesione z /obsluga-hr/raport-pracy-zdalnej.
           [polecenie właściciela 2026-09-06, pkt 4] WŁASNY KLUCZ zamiast dziedziczenia po HrAccess.
           „Przywróć kafelek i zadbaj o niego w uprawnieniach (…) nie da się zmapować uprawnień."
           Kafelek stał na kluczu CAŁYCH kadr, więc wglądu w raporty nie dało się nadać osobno,
           a gdy HrAccess przestał wynikać z rangi — kafelek zniknął administratorowi z panelu.
           Tablica znaczy „wystarczy którykolwiek": drugi klucz trzyma ekran ludziom, których sesja
           została wydana przed wdrożeniem (mają w tokenie jeszcze sam HrAccess). */
        id: "remote-reports",
        slug: "remote-reports",
        title: "Raport pracy zdalnej",
        description: "Wgląd w dzienne raporty pracy zdalnej i konfiguracja obowiązku per pracownik.",
        icon: <FiWifi />,
        permission: ["HrRemoteWorkReports", "HrAccess"],
        category: "org",
        accent: "#0EA5E9",
        tag: "Praca zdalna",
        type: "inline",
        componentPath: "./admin/HrRemoteReportsPage",
    },
    /* ═══ [PAKIET 2026-09-02] Cztery kafelki nowych modułów ═══════════════════════════
       Powstają w FUNDAMENCIE, zanim powstaną ich strony (te są chwilowo stubami „w budowie").
       Powód: pięć modułów budowanych równolegle dopisywałoby się do TEGO pliku jednocześnie
       i wywracałoby sobie nawzajem zmiany przy scalaniu. Bramka każdego kafelka jest DOCELOWA
       od pierwszego dnia — AdminModuleRouter sam pokazuje AccessDeniedScreen bez klucza. */
    {
        // Rejestr wydań: JEDNA strona dla dokumentów zatrudnienia (scope hr) i dokumentów działów
        // (scope dept). Projekt I-a przewidywał drugą stronę „hrIssuedDocs" pod własną trasą —
        // fundament §1.6 scalił obie, bo dwa rejestry tych samych wydań to dubel, nie funkcja.
        // Klucz kafelka to HrAccess (wejście do rejestru); segment „Dokumenty zatrudnienia"
        // ma WŁASNĄ, ostrzejszą bramkę HrDocIssue wewnątrz strony.
        id: "issued-documents-module",
        slug: "issued-documents",
        title: "Rejestr dokumentów wydanych",
        description: "Wydania ze wzorów: umowy i pisma kadrowe oraz dokumenty działów (NDA, pełnomocnictwa, oświadczenia) — z numerem, wersją wzoru i zapisanym plikiem.",
        icon: <FiArchive />,
        permission: "HrAccess",
        category: "org",
        accent: "#4F46E5",
        tag: "Rejestr",
        type: "inline",
        componentPath: "./admin/IssuedDocumentsRegistryPage",
    },
    {
        id: "offboarding-module",
        slug: "offboarding",
        title: "Offboarding",
        description: "Sprawy odejścia pracowników: rozliczenie sprzętu i dostępów, karta obiegowa, zamknięcie akt.",
        icon: <FiUserMinus />,
        permission: "HrOffboardView",
        category: "org",
        accent: "#EF4444",
        tag: "Zatrudnienie",
        type: "inline",
        componentPath: "./admin/OffboardingAdminPage",
    },
    {
        // Projekcja TECHNICZNA onboardingu dla IT — konta, sprzęt, dostępy. Świadomie BEZ danych
        // kadrowych, dlatego stoi na osobnym kluczu ItOnboardHandle (nie na HrOnboardView).
        id: "onboarding-itsm",
        slug: "onboarding",
        title: "Onboarding — realizacja IT",
        description: "Konta, sprzęt i dostępy dla nowych pracowników; przebieg per zgłoszenie.",
        icon: <FiUserPlus />,
        permission: "ItOnboardHandle",
        category: "itam",
        accent: "#0EA5E9",
        tag: "ITSM",
        type: "inline",
        componentPath: "./admin/OnboardingItsmPage",
    },
    {
        id: "job-offers-module",
        slug: "oferty-pracy",
        title: "Oferty pracy",
        description: "Ogłoszenia o naborach: publikacja z zasięgiem widoczności, aplikacje kandydatów i program poleceń pracowniczych.",
        icon: <FiBriefcase />,
        permission: "JobOffersManage",
        // [porządki /admin 2026-09-05] content → org: Nabory prowadzą kadry — obok kandydatów i onboardingu.
        category: "org",
        accent: "#8B5CF6",
        tag: "Rekrutacja",
        type: "inline",
        componentPath: "./admin/JobOffersManagementPage",
    },
    {
        id: "sygnalista-module",
        slug: "sygnalista-handling",
        title: "Obsługa Sygnalisty",
        description: "Compliance — obsługa zgłoszeń naruszeń: statusy, przypisania, anonimowa korespondencja i załączniki. Formularz sygnalisty pozostaje anonimowy.",
        icon: <FiShield />,
        permission: "SygnalistaManage",
        // [porządki /admin 2026-09-05] org → compliance: Zgłoszenia naruszeń: need-to-know, osobny obszar od kadr.
        category: "compliance",
        accent: "#6366F1",
        tag: "Compliance",
        type: "inline",
        componentPath: "./admin/SygnalistaManagementPage",
    },
    /* [polecenie właściciela 2026-09-29] „gdzie mogę dodawać te dokumenty polityki, jak to konfigurować?” — kafelek „Procedury
       i bezpieczeństwo” prowadzi do modułu polityk i procedur spółek (redakcja, zatwierdzenie zarządu, potwierdzenia, pilnowanie).

       [P1 2026-10-01, „Cykl życia na pasku”] tools → compliance. Właściciel 1.10: „Totalnie nie rozumiem działania tego
       /admin/procedury-bezpieczenstwo” — kafelek stał w „Narzędziach i Projektach” obok kalendarza i kanbana, z tą samą tarczą
       i tym samym tagiem co „Wykaz KSC”, więc wyglądał jak kolejny kawałek jednego niezrozumiałego modułu. To obowiązek prawny
       (dokumenty bezpieczeństwa wymagane przez NIS2), więc stoi na półce compliance — z segregatorem i tagiem „Dokumenty”.
       Slug, klucze i type BEZ ZMIAN (linki z powiadomień, AdminModuleRouter, KODY_KAFELKOW_WG_SLUGA).

       Bramka = pięć kluczy modułu; wszystkie need-to-know (NoAdminRankBypass), więc administrator BEZ klucza dostaje kafelek
       ZABLOKOWANY z podpowiedzią (`pokazRandzeBezKlucza`, AdminPanelPage). Siatka dopisuje przed nią „Bez dostępu — ”, dlatego
       zdanie zaczyna się od powodu, a nie od drugiego „nie masz dostępu”. Przycisku do /admin/access-control karta nie ma
       (martwy kafelek bez onClick — AdminPanelPage), więc adres stoi w zdaniu.
       „inline”, nie „route”: kafelek /admin nie gaśnie widocznością modułu (reguła właściciela 22.09). */
    {
        id: "procedury-bezpieczenstwo-module",
        slug: "procedury-bezpieczenstwo",
        title: "Procedury i bezpieczeństwo",
        description: "Polityki i procedury spółek: od napisania, przez zatwierdzenie zarządu, do potwierdzeń pracowników i pilnowania.",
        icon: <FiArchive />,
        permission: ["PolitykiRedakcja", "PolitykiUstawienia", "PolitykiRaportPotwierdzen", "PolitykiRejestrujDecyzje", "PolitykiRejestrujPotwierdzenia"],
        category: "compliance",
        accent: "#6366F1",
        tag: "Dokumenty",
        type: "inline",
        componentPath: "./polityki/PolitykiPage",
        pokazRandzeBezKlucza: true,
        podpowiedzBezKlucza: "ranga administratora go celowo nie daje, bo szkice procedur są poufne. Klucz „Polityki — redakcja” (zmiana ustawień: „Polityki — ustawienia”) nadasz w System i integracje → Uprawnienia i dostęp (/admin/access-control).",
    },
    /* [E8 2026-09-30] „Wykaz KSC” — pilnuje, żeby każda zmiana danych spółki z wykazu podmiotów KSC (adres, zarząd, kontakty, domeny,
       IP, dostawcy IT) trafiła do ministra w 14 dni od dnia zmiany. Bramka = imienny klucz WykazKscPodglad (need-to-know).

       [P1 2026-10-01] tools → compliance, obok procedur — oba obowiązki wyrastają z tej samej ustawy, ale to DWA moduły: inny
       obowiązek (zgłoszenie zmian w 14 dni), więc inna ikona (zegar), inny akcent i inny tag niż procedury. Odpowiedź na pytanie
       właściciela „po co to jest i dlaczego jest tutaj”. Slug, klucz i type bez zmian. */
    {
        id: "wykaz-ksc-module",
        slug: "wykaz-ksc",
        title: "Wykaz KSC",
        description: "Pilnuje, żeby każda zmiana danych spółki (adres, zarząd, domeny, IP, kontakty) trafiła do wykazu ministra w 14 dni.",
        icon: <FiClock />,
        permission: "WykazKscPodglad",
        category: "compliance",
        accent: "#D97706",
        tag: "Obowiązek 14 dni",
        type: "inline",
        componentPath: "./wykazKsc/WykazKscPulpitPage",
        pokazRandzeBezKlucza: true,
        podpowiedzBezKlucza: "ranga administratora go celowo nie daje, bo dane wpisu (adresy IP, domeny, telefony) są poufne. Klucz „Wykaz KSC — podgląd” (zapis: „Wykaz KSC — redakcja”) nadasz w System i integracje → Uprawnienia i dostęp (/admin/access-control).",
    },

    // ═══════════════════════════════════════════════════════════
    // KOMUNIKACJA, TREŚCI I WIEDZA
    // ═══════════════════════════════════════════════════════════
    {
        id: "news-module",
        slug: "news",
        title: "Aktualności",
        description: "Publikowanie postów na blogu firmowym.",
        icon: <FiEdit3 />,
        permission: "NewsManage",
        category: "content",
        accent: "#8B5CF6",
        tag: "Publikacje",
        type: "inline",
        componentPath: "./admin/NewsManagementPage",
    },
    {
        id: "announcements-module",
        slug: "announcements",
        title: "Komunikaty",
        description: "Komunikaty dla pracowników: okno z potwierdzeniem albo pasek nad nagłówkiem aplikacji — dla wybranych osób i w wybranym okresie.",
        icon: <FiBell />,
        permission: "AdminAnnouncements",
        category: "content",
        accent: "#F59E0B",
        tag: "Alerty",
        type: "inline",
        componentPath: "./admin/AnnouncementsManagementPage",
    },
    {
        id: "projects-admin-module",
        slug: "projects-admin",
        title: "Zarządzanie projektami",
        description: "Przegląd wszystkich list projektowych — także usuniętych — z możliwością przywracania.",
        icon: <FiTrello />,
        permission: "ProjectAdminAll",
        category: "tools",
        accent: "#8B5CF6",
        tag: "Projekty",
        type: "inline",
        componentPath: "./admin/PlProjectsAdminPage",
    },
    {
        id: "special-calendars-module",
        slug: "calendars",
        title: "Kalendarze specjalne",
        description: "Tworzenie kalendarzy wydarzeń dla działów i pracowników.",
        icon: <FiCalendar />,
        permission: "AdminCalendars",
        category: "tools",
        accent: "#0EA5E9",
        tag: "Kalendarz",
        type: "inline",
        componentPath: "./admin/SpecialCalendarsManagementPage",
    },
    /* [P-3, 2026-09-09] Kafelek „Dokumenty firmowe" (slug `documents`) ZDJĘTY: dokumenty firmowe są zakładką
       Konfiguracji HR (`hr-config?tab=documents`), a stary adres przekierowuje (AdminModuleRouter). */
    {
        id: "kb-module",
        slug: "knowledge-base",
        title: "Baza wiedzy",
        description: "Konfiguracja kategorii i dostępów do wiki.",
        icon: <FiLayers />,
        permission: "WikiManageConfig",
        category: "content",
        accent: "#A855F7",
        tag: "Wiki",
        type: "inline",
        componentPath: "./configuration/KnowledgeBaseConfigPage",
    },
    {
        id: "faq-module",
        slug: "faq",
        title: "FAQ",
        description: "Zarządzanie pytaniami i odpowiedziami.",
        icon: <FiHelpCircle />,
        permission: "AdminFaq",
        category: "content",
        accent: "#0EA5E9",
        tag: "FAQ",
        type: "inline",
        componentPath: "./configuration/FaqManagementPage",
    },
    {
        id: "gallery-module",
        slug: "gallery",
        title: "Galeria multimedialna",
        description: "Albumy zdjęć z wydarzeń firmowych.",
        icon: <FiImage />,
        permission: "AdminGallery",
        category: "content",
        accent: "#EC4899",
        tag: "Media",
        type: "inline",
        componentPath: "./admin/GalleryManagementPage",
    },
    {
        id: "contests-module",
        slug: "contests",
        title: "Strefa konkursów",
        description: "Tworzenie i zarządzanie konkursami.",
        icon: <FiAward />,
        permission: "AdminContests",
        category: "content",
        accent: "#F97316",
        tag: "Eventy",
        type: "inline",
        componentPath: "./admin/ContestManagementPage",
    },
    {
        id: "surveys-module",
        slug: "surveys",
        title: "Ankiety",
        description: "Tworzenie i zarządzanie ankietami dla pracowników.",
        icon: <FiClipboard />,
        permission: "AdminSurveys",
        category: "content",
        accent: "#7C3AED",
        tag: "Formularze",
        type: "inline",
        componentPath: "./admin/SurveyManagementPage",
    },

    // ═══════════════════════════════════════════════════════════
    // SZKOLENIA I ROZWÓJ (L&D)
    // ═══════════════════════════════════════════════════════════
    {
        id: "elearning-module",
        slug: "elearning",
        title: "E-Learning",
        description: "Platforma szkoleniowa — kursy, lekcje i quizy.",
        icon: <FiBookOpen />,
        permission: "AdminELearning",
        category: "training",
        accent: "#059669",
        tag: "Szkolenia",
        type: "inline",
        componentPath: "./admin/ELearningManagementPage",
    },
    {
        id: "elearning-training-module",
        slug: "elearning-monitoring",
        title: "Monitoring szkoleń",
        description: "Statystyki kursów, wyniki quizów i postępy uczestników.",
        icon: <FiBarChart2 />,
        permission: "AdminCourseStats",
        category: "training",
        accent: "#10B981",
        tag: "Analityka",
        type: "inline",
        componentPath: "./admin/ELearningTrainingPanelPage",
    },

    // ═══════════════════════════════════════════════════════════
    // ITAM I HELPDESK
    // ═══════════════════════════════════════════════════════════
    {
        id: "tickets-module",
        slug: "tickets",
        title: "Zgłoszenia IT",
        description: "Zarządzanie zgłoszeniami serwisowymi IT.",
        icon: <FiMessageSquare />,
        // Trasa /obsluga-it/zgloszenia wymaga TicketView — kafelek gatowany DashboardAccess
        // pokazywał się praktycznie każdemu i prowadził do odmowy.
        permission: "TicketView",
        category: "itam",
        accent: "#059669",
        tag: "Helpdesk",
        type: "route",
        routeKey: "tickets",
    },
    {
        id: "itam-overview",
        slug: "itam-overview",
        title: "Magazyn IT",
        description: "Zbiorczy widok stanu sprzętu, wartości i przypisań.",
        icon: <FiPackage />,
        permission: "ItamDeviceView",
        // [R5, 2026-09-13] Trasa /itam wpuszcza z KTÓRYMKOLWIEK z dwóch kluczy (App.js). Kafelek /admin i przełącznik
        // stron pytają o bramkę TRASY (`czyKafelekDostepny`; r5 [8] = lustro App.js); `permission` zostaje kluczem
        // kafelka dla przycisku „Panel administracyjny” i panelu uprawnień.
        dostepTrasy: ["ItamDeviceView", "ItamInvoiceView"],
        category: "itam",
        accent: "#EAB308",
        tag: "Przegląd ITAM",
        type: "route",
        routeKey: "itamOverview",
    },
    {
        id: "itam-devices",
        slug: "itam-devices",
        title: "Urządzenia IT",
        description: "Pełna lista sprzętu IT i historia urządzeń.",
        icon: <FiMonitor />,
        permission: "ItamDeviceView",
        category: "itam",
        accent: "#F59E0B",
        tag: "Sprzęt",
        type: "route",
        routeKey: "itamDevices",
    },
    {
        id: "itam-licenses",
        slug: "itam-licenses",
        title: "Licencje",
        description: "Zarządzanie licencjami oprogramowania i kluczami.",
        icon: <FiKey />,
        permission: "ItamLicenseView",
        category: "itam",
        accent: "#8B5CF6",
        tag: "Licencje",
        type: "route",
        routeKey: "itamLicenses",
    },
    {
        id: "itam-invoices",
        slug: "itam-invoices",
        title: "Faktury IT",
        description: "Dokumenty zakupowe sprzętu i oprogramowania.",
        icon: <FiFileText />,
        permission: "ItamInvoiceView",
        category: "itam",
        accent: "#D97706",
        tag: "Finanse",
        type: "route",
        routeKey: "itamInvoices",
    },
    {
        id: "itam-protocols",
        slug: "itam-protocols",
        title: "Protokoły",
        description: "Protokoły przekazania i zwrotu sprzętu (on/offboarding IT).",
        icon: <FiClipboard />,
        permission: "ItamDeviceView",
        // [R5, 2026-09-13] Trasa protokołów wpuszcza z KTÓRYMKOLWIEK z dwóch kluczy (App.js) — jak „Magazyn IT”.
        dostepTrasy: ["ItamDeviceView", "ItamInvoiceView"],
        category: "itam",
        accent: "#10B981",
        tag: "Dokumenty IT",
        type: "route",
        routeKey: "itamProtocols",
    },
    {
        id: "itam-teamviewer",
        slug: "itam-teamviewer",
        title: "TeamViewer",
        description: "Ewidencja zdalnych połączeń serwisowych.",
        icon: <FiCast />,
        permission: "ItamTeamViewerView",
        category: "itam",
        accent: "#0EA5E9",
        tag: "Zdalny dostęp",
        type: "route",
        routeKey: "itamTeamviewer",
    },
    {
        id: "drives-module",
        slug: "drives",
        title: "Dyski sieciowe",
        description: "Mapowanie dysków współdzielonych i zarządzanie IT.",
        icon: <FiHardDrive />,
        permission: "DriveManage",
        category: "itam",
        accent: "#64748B",
        tag: "Storage",
        type: "inline",
        componentPath: "./configuration/DrivesPage",
    },

    // ═══════════════════════════════════════════════════════════
    // KONFIGURACJA SYSTEMU I INTEGRACJE
    // ═══════════════════════════════════════════════════════════
    {
        id: "email-signatures-module",
        slug: "email-signatures",
        title: "Stopki e-mail",
        description: "Builder wzorów stopek per firma (bloki, czcionki, kolory, multimedia), dane pracowników i log zmian.",
        icon: <FiEdit3 />,
        permission: "AdminEmailSignatures",
        // [porządki /admin 2026-09-05] config → content: Builder wzorów komunikacji firmowej — treść, nie konfiguracja instalacji.
        category: "content",
        accent: "#6366F1",
        tag: "Komunikacja",
        type: "inline",
        componentPath: "./admin/EmailSignaturesPage",
    },
    {
        id: "integration-hub-module",
        slug: "integrations",
        title: "Microsoft 365 — integracje i skrzynki",
        description: "Tożsamość Azure AD, skrzynki firmowe, routing modułów (ITSM/HR), diagnostyka, licencje i SMS — w jednym miejscu.",
        icon: <FiCloud />,
        permission: "AdminIntegrations",
        category: "config",
        accent: "#0078D4",
        tag: "Integracje",
        type: "inline",
        componentPath: "./admin/IntegrationsAdminPage",
    },
    // [F-4, polecenie właściciela 2026-09-11] KAFELEK „TeamViewer API" USUNIĘTY.
    //
    // „admin/teamviewer-config nie może — nie powinien mieć oddzielnej zakładki, widok
    // do przebudowania. Trzeba scalić i przenieść do /admin/integrations."
    //
    // Konfiguracja (mapowanie grup TeamViewera na spółki, import stacji, dodawanie i usuwanie
    // komputerów) żyje teraz w sekcji „TeamViewer" centrum integracji — czyli tam, gdzie stoją
    // wszystkie inne integracje aplikacji. Sam komponent `TeamViewerConfigPage` NIE ZNIKA:
    // renderuje się w trybie `osadzony`, bez własnego nagłówka strony.
    //
    // Klucz uprawnień `AdminTeamViewerConfig` zostaje nietknięty — dalej bramkuje endpointy
    // konfiguracji, tylko wejście do nich jest jedno zamiast dwóch.
    {
        id: "notifications-module",
        slug: "notifications",
        title: "Powiadomienia",
        description: "Typy powiadomień, alerty e-mail i ustawienia real-time.",
        icon: <FiBell />,
        permission: "AdminNotifications",
        category: "config",
        accent: "#F43F5E",
        tag: "Komunikacja sys.",
        type: "inline",
        componentPath: "./admin/NotificationSettingsPage",
    },
    {
        id: "system-messages-module",
        slug: "system-messages",
        // [OBSZAR 16 2026-09-18] Wzory wiadomości = przypomnienia automatyczne z harmonogramem i kanałem.
        title: "Przypomnienia automatyczne",
        description: "Miłe przypomnienia do pracowników: urodziny, rocznice, zmiana hasła, badania, regulamin — według daty, cyklicznie albo losowo; mailem lub SMS-em.",
        icon: <FiFileText />,
        permission: "AdminSystemMessages",
        // [porządki /admin 2026-09-05] config → content: Szablony komunikatów i maili to treść, którą redaguje komunikacja.
        category: "content",
        accent: "#EC4899",
        // [domknięcie eb 2026-10-04] Jedna nazwa ekranu: „Przypomnienia” (plakietka „Szablony” była trzecią nazwą tego samego ekranu).
        tag: "Przypomnienia",
        type: "inline",
        componentPath: "./admin/SystemMessageTemplatesPage",
    },
    {
        id: "languages-module",
        slug: "languages",
        title: "Języki i tłumaczenia",
        description: "Zarządzanie wersjami językowymi aplikacji.",
        icon: <FiGlobe />,
        permission: "AdminLanguages",
        category: "config",
        accent: "#6366F1",
        tag: "i18n",
        type: "inline",
        componentPath: "../components/Admin/LanguagesManager",
    },
    {
        id: "contact-forms",
        slug: "contact-forms",
        title: "Formularze kontaktowe",
        description: "Zarządzanie formularzami, tematami i przypisaniami routingu.",
        icon: <FiMessageSquare />,
        permission: "AdminContactForms",
        // [porządki /admin 2026-09-05] config → content: Formularze na intranecie: treść i routing pytań pracowników.
        category: "content",
        accent: "#10B981",
        tag: "Routing",
        type: "inline",
        componentPath: "./admin/ContactFormsManagementPage",
    },
    {
        id: "terms-module",
        slug: "terms",
        title: "Regulamin platformy",
        description: "Wersjonowanie polityk, akceptacje użytkowników i logi.",
        icon: <FiFileText />,
        permission: "AdminTerms",
        /* [porządki /admin 2026-09-05] config → compliance: Wersjonowanie polityk i akceptacje — obowiązek prawny.
           [polecenie właściciela 2026-09-18, B] compliance → config: „/admin/terms — przenieś do zakładki /admin
           »System i integracje«." Regulamin platformy jest treścią, którą KAŻDE konto akceptuje przy wejściu do
           aplikacji — to ustawienie instalacji, nie praca działu. Skutki tej jednej linii (wszystkie automatyczne,
           bo kategoria `config` jest JEDYNĄ regułą): wejście tylko dla RANGI administratora i za PIN-em
           (`czyKafelekDostepny`, `AdminModuleRouter`), a kafelek modułu `cmp_terms` ZNIKA z katalogu „Modułów
           obsługi" (wpis zdjęty z KODY_KAFELKOW_WG_SLUGA, mapa nadań i seeder widoczności w ślad za nim —
           zapadka ParytetKafelkowAdminTests.ZakladkiSystemuIIntegracji_NieSaKafelkiemModulu). Sam klucz
           `AdminTerms` i slug zostają — zapisane linki i nadania nie umierają. */
        category: "config",
        accent: "#059669",
        tag: "System / Prawne",
        type: "inline",
        componentPath: "./admin/TermsManagementPage",
    },
    {
        id: "address-book-module",
        slug: "address-book",
        title: "Książka adresowa",
        description: "Synchronizacja i widoczność kontaktów.",
        icon: <FiBook />,
        permission: "AdminAddressBook",
        category: "content",
        accent: "#14B8A6",
        tag: "Kontakty",
        type: "inline",
        componentPath: "./configuration/AddressBookManagementPage",
    },
    /* [polecenie właściciela 2026-09-14, W1] Karty SIM — zestawienia (ta sama treść, co zakładka „Karty SIM” w /admin/address-book).
       [polecenie właściciela 2026-09-15] „przenieś to do sekcji ITAM”: kafelek jest teraz TRASĄ strefy ITAM (/itam/karty-sim),
       a slug „karty-sim” zostaje, żeby stare linki /admin/karty-sim przekierowały (AdminModuleRouter). Bramka trasy = klucz
       odczytu majątku IT ALBO książki adresowej (lustro App.js i polityki KartySimAccess). */
    {
        id: "sim-cards",
        slug: "karty-sim",
        title: "Karty SIM",
        description: "Numery, karty, PIN/PUK, posiadacze i historia; numery ważne i stacjonarne; eksport XLSX/PDF.",
        icon: <FiSmartphone />,
        permission: "AdminAddressBook",
        dostepTrasy: ["ItamDeviceView", "AdminAddressBook"],
        category: "itam",
        accent: "#0EA5E9",
        tag: "Telefony",
        type: "route",
        routeKey: "itamSim",
    },
    {
        id: "categories-module",
        slug: "categories",
        title: "Konfigurator zgłoszeń",
        description: "Kreator kafelków, moduły obsługi, kategorie, pola formularza i przekazania.",
        icon: <FiTag />,
        permission: "CategoryManage",
        // [porządki /admin 2026-09-05] config → itam: Kreator kafelków i modułów OBSŁUGI ZGŁOSZEŃ — konfiguracja helpdesku, nie systemu.
        category: "itam",
        accent: "#6366F1",
        tag: "Zgłoszenia",
        type: "inline",
        componentPath: "./admin/CategoriesPage",
    },
    {
        id: "analytics-module",
        slug: "analytics",
        // [S2, polecenie właściciela 2026-09-13] „/admin/analytics jest częścią ITAM" — ale tytuł „Analityka i czas
        // pracy" sugerował, że czas pracy (aktywność O365) też tu leży. To analityka ZGŁOSZEŃ i ewidencji czasu w zgłoszeniach.
        title: "Analityka zgłoszeń i ewidencji",
        description: "Raporty pracy serwisu — zgłoszenia, czasy realizacji, ewidencja czasu (CSV), raport zbiorczy do 100% czasu pracy.",
        icon: <FiTrendingUp />,
        permission: "ReportsView",
        /* [Obszar 22, polecenie właściciela 2026-09-18] O wejściu na /admin/analytics decyduje ZAKRES liczony przez
           serwer (ReportsView ALBO czynny członek Zarządu ALBO kierowanie działem realizującym ALBO imienny
           MOD.<KOD>.REPORTS z kafelka modułu), nie sam klucz. BYŁO ŹLE: router odmawiał wejścia każdemu bez
           `ReportsView`, więc pracownik modułu z kafelkiem „Analityka" (od dziś: klucz JEDNEGO modułu, nie globalny)
           i Prezes bez klucza widzieli kafelek na landingu, a po kliknięciu ekran „brak uprawnień". Strona sama
           pyta serwer i pokazuje odmowę (403), gdy zakres jest pusty — router ma ją wpuścić. `permission` zostaje
           kluczem kafelka na siatce /admin (siatka nie zna zakresu serwera; wejście z landingu modułu jest po zakresie). */
        zakresZSerwera: true,
        category: "itam",
        accent: "#6366F1",
        tag: "Analityka",
        type: "inline",
        componentPath: "./admin/AnalyticsPage",
    },
    // ── NARZĘDZIA / MONITORING (na samym dole panelu) ──────────────
    {
        id: "worktime-analytics-module",
        slug: "worktime-analytics",
        title: "Analityka czasu pracy",
        description: "Aktywność, obecność i czas pracy zespołu — raporty i statystyki.",
        icon: <FiBarChart2 />,
        permission: "AdminWorkTime",
        // [S2, polecenie właściciela 2026-09-13] „kafelek z zakresu /admin/worktime-analytics przenieś do sekcji Compliance
        // i zgodność — to nie jest element ITAM". Slug i klucz bez zmian; lustro: moduleAdminTiles.hr_worktime_analytics.
        category: "compliance",
        accent: "#0EA5E9",
        tag: "Monitoring",
        type: "inline",
        componentPath: "./admin/WorkTimeAnalyticsPage",
    },
    {
        id: "system-health-module",
        slug: "system-health",
        title: "Stan aplikacji",
        description: "Down-detector, SLA/stabilność i odczyt logów — błędy, restarty i przyczyny padnięć aplikacji.",
        icon: <FiActivity />,
        permission: "AdminSystemHealth",
        category: "config",
        accent: "#EF4444",
        tag: "Monitoring",
        type: "inline",
        componentPath: "./admin/SystemHealthPage",
    },
    // ── TOŻSAMOŚĆ APLIKACJI / BRANDING (na samym dole panelu) ──────
    {
        id: "branding-module",
        slug: "branding",
        title: "Tożsamość aplikacji",
        description: "Logo, favicon, ikona, tytuł i dane meta (podgląd linków) — globalny branding aplikacji.",
        icon: <FiImage />,
        permission: "AdminBranding",
        category: "config",
        accent: "#EC4899",
        tag: "Branding",
        type: "inline",
        componentPath: "./admin/BrandingPage",
    },
    // ── REJESTRACJA / ONBOARDING (na samym dole panelu) ──────────
    {
        id: "registration-config-module",
        slug: "registration-config",
        title: "Rejestracja i onboarding",
        /* [polecenie właściciela 2026-09-21] Opis kafelka NIE OBIECUJE JUŻ KLAUZULI RODO — jej edytor
           zszedł do dokumentów prawnych (kafelek „Regulamin platformy"), a formularz pierwszego logowania
           pokazuje dokładnie tę samą treść, co /dokumenty/rodo. Kafelek, który zapowiada ekran, którego
           nie ma, wysyła administratora szukać usterki tam, gdzie jej nie ma. */
        description: "Tekst formularza konfiguracji konta przy pierwszym logowaniu.",
        icon: <FiShield />,
        permission: "AdminRegistration",
        /* [porządki /admin 2026-09-05] config → org: Klauzula RODO i formularz pierwszego logowania — proces kadrowy przyjęcia.
           [polecenie właściciela 2026-09-18, A] org → config: „/admin/registration-config — to przenieś mi do sekcji
           /admin »System i integracje«, ponieważ jest to komunikat w zakresie 1. logowania w życiu w aplikacji
           użytkownika." Klauzula i treść formularza pierwszego logowania dotyczą KAŻDEGO konta w instalacji, nie
           jednego działu. Kategoria `config` znaczy: tylko ranga administratora, za PIN-em, bez kafelka modułu —
           kafelek `hr_registration_config` schodzi z katalogu „Modułów obsługi" razem z wpisem w mapie nadań
           i wierszem seedera (istniejące wiersze w bazie zostają, kod jest nietykalny). Sąsiednia zakładka
           „Wnioski rejestracji" (ten sam klucz AdminRegistration) ZOSTAJE w kadrach — to kolejka pracy, nie komunikat. */
        category: "config",
        accent: "#6366F1",
        tag: "Onboarding",
        type: "inline",
        componentPath: "./admin/RegistrationConfigPage",
    },
    {
        id: "registration-requests-module",
        slug: "registration-requests",
        title: "Wnioski rejestracji",
        description: "Wnioski konfiguracji kont pracowników — zatwierdzanie, przekierowanie i reset.",
        icon: <FiUserPlus />,
        permission: "AdminRegistration",
        category: "org",
        accent: "#3B82F6",
        tag: "Onboarding",
        type: "inline",
        componentPath: "./admin/RegistrationRequestsPage",
    },
    {
        // [brief 2026-08-31, pkt 8] ODBIORCA obiegu „kadry proszą → IT zakłada → system łączy".
        // Bez tego kafelka prośba kadr nie miała gdzie się pojawić: kadrowa dostawała komunikat
        // „prośba poszła do działu IT", a w IT nie było ani ekranu, ani zadania, ani powiadomienia.
        // Uprawnienie AdminUsersView — kto zakłada konta, ten obsługuje te prośby.
        id: "konta-zgloszenia-module",
        slug: "account-requests",
        title: "Prośby o konta",
        description: "Kadry proszą o założenie konta dla osoby bez konta w aplikacji.",
        icon: <FiUserPlus />,
        permission: "AdminUsersView",
        category: "access",
        accent: "#6366F1",
        tag: "Onboarding",
        type: "inline",
        componentPath: "./admin/KontaZgloszeniaPage",
    },
    {
        /* [polecenie właściciela 2026-09-22 wieczór] TEN KAFELEK JEST JUŻ GŁÓWNYM WEJŚCIEM.
           „sekcje /struktura-organizacyjna przenieś do /admin/struktura-organizacyjna — zrób dla tego
            kafelek i ma to być dostępne w sekcji admin, z access-control WYŁĄCZ WIDGET WYŁĄCZANIA
            dla /struktura-organizacyjna — to uprawnienia administracyjne mają być."

           „sekcje /struktura-organizacyjna przenieś do /admin/struktura-organizacyjna — zrób dla tego
            kafelek i ma to być dostępne w sekcji admin, z access-control WYŁĄCZ WIDGET WYŁĄCZANIA
            dla /struktura-organizacyjna — to uprawnienia administracyjne mają być."

           Pozycja paska bocznego zeszła, a trasa przeniosła się pod /admin/struktura-organizacyjna
           (stary adres przekierowuje) — więc TEN kafelek jest od 2026-09-22 głównym wejściem do edytora.
           Bramką jest sam klucz `OrgStructureView`; szkice reorganizacji chroni `OrgStructurePlan`
           po stronie API. Kafelek jest typu "route": strona ma własne, pełne hero i własną trasę. */
        id: "org-structure-module",
        slug: "org-structure",
        title: "Struktura organizacyjna",
        description: "Schemat organizacyjny firmy, obsada stanowisk i planowanie zmian (szkice reorganizacji).",
        icon: <FiGitMerge />,
        permission: "OrgStructureView",
        category: "org",
        accent: "#0EA5E9",
        tag: "Struktura",
        type: "route",
        routeKey: "orgStructure",
        /* ⚠️ BEZ `moduleCode` — i to nie jest przeoczenie. [R5, 2026-09-13] stało tu
           `moduleCode: "orgStructure"`, bo trasa pytała TAKŻE o widoczność modułu, a kafelek ma pytać
           o DOKŁADNIE to samo, co trasa (zapadka `adminTilesConfig.przelacznik.r5` [7] liczy to wprost:
           kafelek deklaruje `moduleCode` wtedy i tylko wtedy, gdy trasa stoi na `restrictedModule`).
           Od 2026-09-22 trasa nie pyta o widoczność wcale, więc pytanie o nią tutaj gasiłoby kafelek,
           na który trasa i tak by wpuściła — a to jest ten sam rozjazd, tylko odwrócony.
           SKUTEK UBOCZNY, NAZWANY WPROST: to był OSTATNI kafelek katalogu z `moduleCode`, więc reguła
           „kafelek zgaszony modułem" (`czyKafelekUkrytyModulem`) została bez instancji — mierzy ją
           teraz wprost zapadka `kafelekZablokowanyWidocznoscia.o23` (sekcja 1–2). */
    },
    {
        // [brief 2026-09-01, pkt J] MIGRACJA DANYCH między środowiskami (testowe → produkcyjne).
        // Kafelek powstaje TUTAJ z góry, razem z kluczem AdminDbMigration w katalogu uprawnień —
        // moduł budowany równolegle podpina się pod gotowe wejście, zamiast dokładać kafelek
        // w tym samym pliku (i wywracać cudzą zmianę przy scalaniu).
        // Operacja jest NIEODWRACALNA, dlatego ma własny klucz, a nie ConfigManage.
        id: "db-migration-module",
        slug: "db-migration",
        title: "Migracja bazy danych",
        description: "Przeniesienie danych ze środowiska testowego na produkcyjne — wybór zakresu, podgląd różnic i wykonanie.",
        icon: <FiRefreshCw />,
        permission: "AdminDbMigration",
        category: "config",
        accent: "#DC2626",
        tag: "Wdrożenia",
        type: "inline",
        componentPath: "./admin/DbMigrationPage",
    },
    /* [OBSZAR 18, polecenie właściciela 2026-09-18] PODGLĄD KONTA — „zaloguj jako" BEZ wchodzenia na konto.
       Właściciel: „admin w sekcji »System i integracje« może […] wybrać użytkownika z listy […] zobaczyć, co
       widzi i jakie ma uprawnienia wybrany user […] tylko admin to może […] imiennym przypisaniem".
       Kategoria `config` = tylko ranga administratora + PIN (czyKafelekDostepny / AdminModuleRouter), a klucz
       `PodgladKontaAdmin` jest need-to-know (EXPLICIT_ONLY_PERMS w AuthContext = lustro backendu), więc ranga
       sama kafelka NIE zapala — trzeba nadać klucz imiennie w „Uprawnienia i dostęp". Backend pyta o OBIE bramki.
       Tokenu na cudze konto NIE MA — to warstwa odczytu (uprawnienia ze źródłem, moduły, kafelki, ranga, PIN),
       a dane osobowe są maskowane na serwerze. */
    {
        id: "podglad-konta-module",
        slug: "podglad-konta",
        title: "Podgląd konta („zaloguj jako”)",
        description: "Co widzi i jakie ma uprawnienia wybrana osoba: klucze ze źródłem, moduły, kafelki panelu, ranga i PIN — bez wchodzenia na konto, z danymi osobowymi zamaskowanymi na serwerze. Każde otwarcie trafia do dziennika.",
        icon: <FiEye />,
        permission: "PodgladKontaAdmin",
        category: "config",
        accent: "#0EA5E9",
        tag: "Bezpieczeństwo",
        type: "inline",
        componentPath: "./admin/PodgladKontaPage",
    },
];

// ── Helpery ─────────────────────────────────────────────────────

/**
 * [zgłoszenie właściciela 2026-09-16] „Sprawdź dlaczego Konfiguracja HR … i Raport pracy zdalnej … brak w katalogu."
 *
 * KLUCZE KAFELKA — ZAWSZE LISTA NAPISÓW. Pole `permission` wolno zapisać jako napis (jeden klucz) albo jako
 * TABLICĘ („wystarczy którykolwiek" — tak czyta ją `hasPermission`, `ProtectedRoute` i ekran odmowy).
 *
 * BYŁO ŹLE: każdy, kto potrzebował klucza kafelka, rozbijał tablicę SAM albo — częściej — nie rozbijał wcale.
 * Tablica uprawnień pytała mapę katalogu CAŁYM polem (`metaWgKlucza.get(["HrAccess","AdminDocuments"])`), a mapa
 * trzyma napisy, więc trafienia nie było NIGDY: oba kafelki z tablicą dostawały plakietkę „brak w katalogu",
 * ich kratki były martwe w obu zakładkach, a kolumna „Skąd ma" pokazywała kreskę nawet komuś, kto klucz miał.
 * Widoczny na ekranie napis „HrAccess,AdminDocuments" nie istniał w kodzie — to `Array.prototype.toString`.
 *
 * TERAZ: JEDNA funkcja odpowiada na pytanie „jakimi kluczami otwiera się ten kafelek", i wołają ją wszyscy.
 * Pierwszy klucz jest WIODĄCY (najwęższy, powołany dla tego ekranu); kolejne to alternatywy — zwykle szersze.
 */
export const kluczeKafelka = (tile) => {
    const p = tile?.permission;
    if (!p) return [];
    return (Array.isArray(p) ? p : [p]).filter(Boolean).map(String);
};

/** Klucz WIODĄCY kafelka — ten, który nadaje się, gdy chodzi dokładnie o to jedno wejście. */
export const kluczWiodacyKafelka = (tile) => kluczeKafelka(tile)[0] || null;

/**
 * [zgłoszenie właściciela 2026-09-16] „za wyjątkiem System i integracje — to ma dostęp tylko admin i nikt więcej,
 * tutaj potrzeba rangi".
 *
 * Kategoria, w której leży administracja SAMEJ INSTALACJI. Trzy skutki, wszystkie w jednym miejscu:
 *  • kafelki tej kategorii otwiera WYŁĄCZNIE ranga administratora (`czyKafelekDostepny`),
 *  • tylko one stoją za bramką PIN (reszta panelu jej nie potrzebuje — patrz `AdminModuleRouter`),
 *  • nie są wypisywane w „Modułach obsługi" — nie przypisuje się ich działowi (patrz `moduleAdminTiles.js`).
 */
export const KATEGORIA_TYLKO_RANGA = "config";

/** Czy wejście do tego kafelka wymaga RANGI administratora (a nie samego klucza). */
export const czyKafelekTylkoDlaRangi = (tile) => tile?.category === KATEGORIA_TYLKO_RANGA;

/**
 * Ranga administratora liczona z obiektu `auth` z `useAuth()`.
 * ⚠️ Wołający przekazują RÓŻNE wycinki kontekstu (jedni `isAdmin`, inni całe `user`), więc pytamy o oba —
 * i domyślnie odmawiamy. Fail-closed: brak informacji o randze ma znaczyć „nie administrator", bo ta funkcja
 * strzeże kategorii, w której leży migracja bazy i nadawanie uprawnień.
 */
export const czyRangaAdministratora = (auth) => auth?.isAdmin === true || auth?.user?.type === 3;

/**
 * ═══ [polecenie właściciela 2026-09-19] JEDNA REGUŁA PORZĄDKU MODUŁÓW W SEKCJI ═══
 *
 * „Możesz mi robić tak, że te moduły w sekcji admin — do danej sekcji będą automatycznie ułożone
 *  alfabetycznie i tak samo jak są tutaj ułożone będą one ułożone analogicznie w access-control
 *  w sekcji Moduły obsługi → zakładka kafelki?"
 *
 * WŁAŚCICIEL PROSI O PORÓWNYWANIE DWÓCH EKRANÓW. Gdyby każdy z nich sortował się własną kopią tej
 * samej reguły, pierwsza zmiana (inny locale, inne pole z nazwą, inna obsługa pustych) rozjechałaby
 * je po cichu — a człowiek zobaczyłby dwie różne listy tych samych modułów i nie miałby czego
 * porównywać. To jest w tym repozytorium udokumentowana klasa wady („jedna dana = jedno miejsce",
 * paczki BC/BD/BE). Dlatego reguła stoi TU, raz, i wołają ją OBA ekrany:
 *  • siatka kafelków /admin (AdminPanelPage — układ w obrębie kategorii),
 *  • katalog „Moduły obsługi" → „Kafelki landingu" (constants/moduleAdminTiles → MODULE_ADMIN_TILE_GROUPS,
 *    z którego `ModuleTilesAccessView` liczy `pageGlobalTiles`, a landingi modułów — zestawy domyślne).
 *
 * ⚠️ SORTUJEMY PO POLSKU. `Array.prototype.sort()` bez porównywarki układa po kodach UTF-16, więc
 * „Łączność", „Ósemka" i „Świat" wylądowałyby ZA „Żaba" — na końcu listy, poza swoim miejscem
 * w alfabecie. `localeCompare(…, "pl")` stawia Ł po L, Ó po O, Ś po S (sprawdzone w zapadce).
 *
 * ⚠️ LOCALE „pl" JEST PODANE WPROST, A NIE WZIĘTE Z MASZYNY. `localeCompare(b)` bez drugiego
 * argumentu pyta o ustawienia środowiska — a stacja robocza, na której to powstaje, mówi `pl-PL`
 * (zmierzone 2026-09-19: `new Intl.Collator().resolvedOptions().locale` → „pl-PL"). Taka wersja
 * wygląda tu na poprawną i rozjeżdża się dopiero na serwerze albo w przeglądarce ustawionej
 * inaczej — czyli u właściciela, nie u autora. Zapadka pyta wprost, o jaki język prosi ta reguła.
 *
 * ⚠️ DWA POLA NA JEDNĄ NAZWĘ. Kafelek panelu ma `title`, kafelek katalogu modułów — `label`
 * (ten drugi jest przepisywany z pierwszego). Jedna porównywarka musi rozumieć oba kształty,
 * inaczej „wspólna funkcja" i tak działałaby tylko po jednej stronie. Goły napis też jest
 * poprawnym wejściem — inaczej lista samych nazw „posortowałaby się" na samych pustych ciągach,
 * czyli wcale (na tym potknęła się pierwsza wersja zapadki).
 *
 * ⚠️ KOLEJNOŚĆ KATEGORII ZOSTAJE NIETKNIĘTA — właściciel prosił o porządek MODUŁÓW W SEKCJI.
 * Sekcje /admin dalej idą w kolejności kluczy `ADMIN_CATEGORIES` (to decyzja z 2026-09-05).
 */
export const nazwaModulu = (modul) =>
    (typeof modul === "string" ? modul : String(modul?.title ?? modul?.label ?? ""));

/** JEDYNA porównywarka porządku modułów w sekcji — alfabetycznie, po polsku. */
export const porownajModuly = (a, b) => nazwaModulu(a).localeCompare(nazwaModulu(b), "pl");

/**
 * Kopia listy ułożona tą regułą. NIE mutuje wejścia — woła ją m.in. `MODULE_ADMIN_TILE_GROUPS`
 * na tablicach składanych z `ADMIN_TILES`, a przestawienie katalogu w miejscu zmieniłoby kolejność
 * wszystkim innym czytelnikom (przełącznik stron, parytet z backendem).
 * Sort jest stabilny (ES2019), więc moduły o identycznej nazwie zachowują kolejność katalogu.
 */
export const posortujModuly = (lista) => [...(lista || [])].sort(porownajModuly);

/** Filtruj kafelki per kategoria */
export const getTilesByCategory = (category) =>
    ADMIN_TILES.filter((t) => t.category === category);

/** Pobierz kafelek po slug */
export const getTileBySlug = (slug) =>
    ADMIN_TILES.find((t) => t.slug === slug);

/** Pobierz wszystkie slagi inline modułów per kategoria */
export const getTabsForCategory = (category) =>
    ADMIN_TILES
        .filter((t) => t.category === category)
        .map(toTab);

const toTab = (t) => ({
    id: t.slug,
    slug: t.slug,
    label: t.title.length > 20 ? t.title.substring(0, 18) + "…" : t.title,
    // [R5] Pełny tytuł — lista rozwijana ma miejsce na całą nazwę; skrót `label` zostaje dla wąskich pasków.
    title: t.title,
    icon: t.icon,
    isRoute: t.type === "route",
    routeKey: t.routeKey,
    permission: t.permission,
    category: t.category,
});

/**
 * [R5, polecenie właściciela 2026-09-13] „Menu dostępne do przełączania, przełączaj względem podziału na moduły
 * oraz uprawnienia jakie posiada pracownik (…) z uwzględnieniem indywidualnych uprawnień albo per dział albo
 * prywatnie nadanych indywidualnie."
 *
 * JEDEN predykat dostępu do kafelka — ten sam, którym /admin rysuje kafelki (klucz przez `hasPermission`:
 * uprawnienia efektywne = dział ∪ imienne ∪ auto-nadania, skrót rangi administratora, klucze jawnie odebrane)
 * i którym ProtectedRoute wpuszcza na trasę docelową (widoczność modułu, jeśli trasa na niej stoi — `moduleCode`).
 * Kafelek bez klucza NIE jest dostępny nikomu: bramka musi istnieć (adminTilesConfig.dostep.test.js).
 *
 * @param {object} tile kafelek z ADMIN_TILES
 * @param {{hasPermission: Function, isModuleRestricted?: Function, isModuleGloballyHidden?: Function}} auth z useAuth()
 */
export const czyKafelekDostepny = (tile, auth) => {
    if (!tile || !tile.permission || !auth || typeof auth.hasPermission !== "function") return false;
    /* [zgłoszenie właściciela 2026-09-16] „System i integracje — to ma dostęp tylko admin i nikt więcej,
       tutaj potrzeba rangi". Ta kategoria to administracja SAMEJ INSTALACJI: integracje, powiadomienia,
       języki, stan aplikacji, tożsamość, migracja bazy i nadawanie uprawnień. Klucz nie wystarczy — a że
       dwa z tych kluczy (AdminLanguages, AdminBranding) backend i tak egzekwuje RANGĄ, dotąd kafelek
       obiecywał wejście, którego API nie dawało. Teraz obie strony mówią to samo. */
    if (czyKafelekTylkoDlaRangi(tile) && !czyRangaAdministratora(auth)) return false;
    // Kafelek z własnym adresem może mieć bramkę trasy szerszą niż klucz kafelka (`dostepTrasy` = lustro
    // requiredPermission z App.js) — przełącznik prowadzi na trasę, więc pyta o to, o co pyta ProtectedRoute.
    if (!auth.hasPermission(tile.dostepTrasy || tile.permission)) return false;
    if (tile.moduleCode) {
        if (typeof auth.isModuleRestricted === "function" && auth.isModuleRestricted(tile.moduleCode)) return false;
        if (typeof auth.isModuleGloballyHidden === "function" && auth.isModuleGloballyHidden(tile.moduleCode)) return false;
    }
    return true;
};

/**
 * DLACZEGO ten kafelek jest niedostępny — JEDNO zdanie po polsku, albo `null`, gdy jest dostępny.
 *
 * [polecenie właściciela 2026-09-19] „w trybie Oglądasz konto pokazuj wszystkie kafelki z kategorii, a te,
 * do których pracownik nie ma dostępu, zrób szare" — szary kafelek bez POWODU nie mówi nic: właściciel testuje
 * konto i musi wiedzieć, czy brakuje klucza, czy moduł jest schowany. To samo zdanie czyta ekran podglądu konta
 * („Brakuje (N)” na /admin/podglad-konta).
 *
 * ⚠️ JEDNA DANA = JEDNO MIEJSCE. Powód mieszkał dotąd wyłącznie w `PodgladKontaPage.ocenKafelek` i był liczony
 * z ODPOWIEDZI SERWERA. Gdyby panel /admin dorobił sobie drugi zestaw zdań, pierwsza zmiana reguły dostępu
 * rozjechałaby oba ekrany — a to dokładnie ta klasa wady, którą właściciel zgłaszał już cztery razy z różnych
 * ekranów. Dlatego powód stoi TU, obok predykatu, którego jest wyjaśnieniem, i przyjmuje ten sam kształt `auth`.
 *
 * KOLEJNOŚĆ ODPOWIADA `czyKafelekDostepny` — zwracamy PIERWSZĄ napotkaną przeszkodę, tę samą, o którą potknął się
 * predykat. Inna kolejność dałaby zdanie prawdziwe, ale nie to, które faktycznie zamknęło drzwi.
 *
 * @param {object} tile kafelek z ADMIN_TILES
 * @param {{hasPermission: Function, isAdmin?: boolean, user?: object, isModuleRestricted?: Function, isModuleGloballyHidden?: Function}} auth
 * @returns {string|null}
 */
export const powodBrakuKafelka = (tile, auth) => {
    if (!tile || !tile.permission) return "kafelek bez bramki uprawnień";
    if (czyKafelekDostepny(tile, auth)) return null;
    if (!auth || typeof auth.hasPermission !== "function") return "nieznany stan uprawnień";
    if (czyKafelekTylkoDlaRangi(tile) && !czyRangaAdministratora(auth)) return "wymaga rangi administratora";
    const wymagane = tile.dostepTrasy || tile.permission;
    if (!auth.hasPermission(wymagane)) return `brak klucza ${kluczeKafelka({ permission: wymagane }).join(" lub ")}`;
    if (tile.moduleCode && typeof auth.isModuleRestricted === "function" && auth.isModuleRestricted(tile.moduleCode)) {
        return `moduł „${tile.moduleCode}” ukryty dla tego konta`;
    }
    if (tile.moduleCode && typeof auth.isModuleGloballyHidden === "function" && auth.isModuleGloballyHidden(tile.moduleCode)) {
        return `moduł „${tile.moduleCode}” ukryty dla całej firmy`;
    }
    return "niedostępny";
};

/**
 * [Obszar 22, 2026-09-18] Czy ROUTER /admin/<slug> ma odmówić wejścia na zakładkę. Ten sam predykat co siatka
 * (`czyKafelekDostepny`), z jednym wyjątkiem: zakładka, której dostęp liczy SERWER (`zakresZSerwera`), nie jest
 * bramkowana kluczem na froncie — strona sama pokazuje odmowę serwera. Bez tego kafelek analityki nadany przy
 * module (klucz MOD.<KOD>.REPORTS) i Prezes bez ReportsView lądowali na „brak uprawnień" mimo zakresu po stronie API.
 * Ranga „System i integracje" i widoczność modułu obowiązują nadal (predykat siatki niżej sprawdza je jako pierwsze).
 */
export const czyRouterOdmawiaKafelka = (tile, auth) => {
    if (!tile || !tile.permission) return false;
    if (tile.zakresZSerwera) {
        if (czyKafelekTylkoDlaRangi(tile) && !czyRangaAdministratora(auth)) return true;
        if (tile.moduleCode && auth) {
            if (typeof auth.isModuleRestricted === "function" && auth.isModuleRestricted(tile.moduleCode)) return true;
            if (typeof auth.isModuleGloballyHidden === "function" && auth.isModuleGloballyHidden(tile.moduleCode)) return true;
        }
        return false;
    }
    return !czyKafelekDostepny(tile, auth);
};

/**
 * [R5] Grupy przełącznika stron panelu: STREFA (kategoria /admin) → strony, do których ta osoba ma wejście.
 * Strefa otwartej strony idzie PIERWSZA („jak coś jest w zakresie ITAM, to znaczy, że masz to tu pokazywać
 * elementy ITAM jakie mam dostęp, jak to inna strefa to inna strefa"), pozostałe w kolejności sekcji /admin.
 * Strefa bez ani jednej dostępnej strony nie pojawia się wcale — pusty nagłówek nic nie mówi.
 *
 * `dodatkowe` — strony z własną trasą, które nie są kafelkami panelu (np. nowy onboarding HR na pasku kartoteki),
 * w kształcie kafelka (slug, title, icon, permission, category, type: "route", routeKey, opcjonalnie moduleCode).
 * Przechodzą przez TEN SAM predykat i stają na końcu swojej strefy; slug kafelka ma pierwszeństwo.
 *
 * @returns {Array<{id: string, label: string, color: string, tabs: Array}>}
 */
export const grupyPrzelacznika = (auth, biezacaKategoria, dodatkowe = []) => {
    const kolejnosc = Object.keys(ADMIN_CATEGORIES);
    const posortowane = biezacaKategoria && kolejnosc.includes(biezacaKategoria)
        ? [biezacaKategoria, ...kolejnosc.filter((k) => k !== biezacaKategoria)]
        : kolejnosc;
    const strony = [
        ...ADMIN_TILES,
        ...(dodatkowe || []).filter((d) => d && d.slug && d.title && !ADMIN_TILES.some((t) => t.slug === d.slug)),
    ];
    return posortowane
        .map((id) => ({
            id,
            label: ADMIN_CATEGORIES[id].label,
            color: ADMIN_CATEGORIES[id].color,
            tabs: strony.filter((t) => t.category === id && czyKafelekDostepny(t, auth)).map(toTab),
        }))
        .filter((g) => g.tabs.length > 0);
};

/**
 * [R5, przegląd 2026-09-13] KAFELKI STRONY GŁÓWNEJ /admin — ten sam predykat co przełącznik stron (`czyKafelekDostepny`).
 *
 * BYŁO ŹLE: /admin rysował kafelki samym `hasPermission(tile.permission)`, bez bramki trasy i bez widoczności modułu.
 * Osoba z samym ItamInvoiceView miała na /admin jeden kafelek ITAM, a w przełączniku trzy strony; osoba z ukrytym
 * modułem struktury widziała kafelek, który kończył się ekranem „Moduł ukryty”, choć przełącznik go chował.
 * Dwie reguły dostępu na dwóch ekranach tego samego panelu — teraz jedna (pilnuje adminPanelKafelkiJakPrzelacznik.r5).
 *
 * (Usunięte `getAccessibleTabs` — druga reguła „sam klucz, bez trasy i modułu” — nie miało już wywołań w aplikacji.)
 */
export const kafelkiPanelu = (auth) => ADMIN_TILES.filter((t) => czyKafelekDostepny(t, auth));

/**
 * [polecenie właściciela 2026-09-29, „Procedury i bezpieczeństwo”] KAFELKI-PODPOWIEDZI DLA RANGI ADMINISTRATORA.
 *
 * Kafelek z `pokazRandzeBezKlucza` stoi na kluczach need-to-know, których ranga administratora celowo NIE otwiera. Bez tej
 * funkcji administrator bez klucza nie widział modułu NIGDZIE i pytał, gdzie się go konfiguruje. Teraz dostaje go w siatce
 * /admin jako ZABLOKOWANY (martwy — bez kliknięcia, bez wyszukiwarki), z powodem = `podpowiedzBezKlucza`, czyli co nadać.
 *
 * ⚠️ To WIDOK, nie dostęp (A15-06): predykat dostępu (`czyKafelekDostepny`) jest nietknięty — kafelek wchodzi tu wyłącznie
 * wtedy, gdy dostępu NIE ma. Tylko ranga administratora (ona jedna nadaje klucze), tylko przy widocznym module, i nigdy pod
 * nakładką podglądu konta (tam szare kafelki liczy `powodBrakuKafelka` dla oglądanej osoby).
 *
 * @returns {Array<object>} kafelki z polami `dostepny: false` i `powod`
 */
export const kafelkiPodpowiedziDlaRangi = (auth) => {
    if (!czyRangaAdministratora(auth)) return [];
    return ADMIN_TILES
        .filter((t) => t.pokazRandzeBezKlucza && !czyKafelekDostepny(t, auth))
        .filter((t) => !t.moduleCode || !(
            (typeof auth.isModuleRestricted === "function" && auth.isModuleRestricted(t.moduleCode))
            || (typeof auth.isModuleGloballyHidden === "function" && auth.isModuleGloballyHidden(t.moduleCode))))
        .map((t) => ({ ...t, dostepny: false, powod: t.podpowiedzBezKlucza || powodBrakuKafelka(t, auth) }));
};

/**
 * [T2b, 2026-09-14] PRZYCISK „Panel administracyjny” (menu boczne i nagłówek) — TEN SAM predykat co kafelki /admin.
 *
 * BYŁO ŹLE: przycisk liczył `hasAnyAdminTileAccess` (sam `tile.permission`), a /admin i przełącznik stron — `czyKafelekDostepny`
 * (bramka TRASY + widoczność modułu). Osoba z samym OrgStructureView i ukrytym modułem struktury dostawała przycisk,
 * a po kliknięciu pusty panel; dwie reguły na jedno pytanie „czy jest tu dla mnie cokolwiek”.
 *
 * @param {{hasPermission: Function, isModuleRestricted?: Function, isModuleGloballyHidden?: Function}} auth z useAuth()
 */
export const czyPokazacPanelAdministracyjny = (auth) => kafelkiPanelu(auth).length > 0;

/**
 * [brief 2026-09-01, pkt L10] Czy ta osoba ma dostęp DO CZEGOKOLWIEK w panelu administracyjnym.
 * Odpowiedź decyduje o tym, czy w menu i w nagłówku pojawi się przycisk „Panel administracyjny" —
 * właściciel: „jak ktoś nie ma uprawnień do sekcji admina, to trzeba nadać — pojawi się przycisk
 * Panel admina". Wcześniej przycisk zależał WYŁĄCZNIE od rangi konta (type === 3), więc nadanie
 * klucza kafelka nie dawało do niego żadnej drogi poza wklejeniem adresu z pamięci.
 */
export const hasAnyAdminTileAccess = (hasPermission) =>
    ADMIN_TILES.some((t) => !!t.permission && hasPermission(t.permission));
